/* Shared configurator updates: no permanent polling or text-mutation feedback. */
const aynaConfiguratorUpdates = (() => {
  const callbacks = new Set();
  let pending = false;
  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      callbacks.forEach((callback) => callback());
    });
  };
  const observe = (callback) => { callbacks.add(callback); };
  const observer = new MutationObserver((records) => {
    if (records.some((record) => [...record.addedNodes, ...record.removedNodes]
      .some((node) => node.nodeType === 1))) schedule();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  ['input', 'change', 'variant:change', 'variant:update', 'product:variant-change',
    'shopify:section:load'].forEach((type) => document.addEventListener(type, schedule));
  return { observe, schedule };
})();

(() => {
  const NAME_SELECTOR = 'input[name="properties[Prénom ou mot à créer]"]';
  const COLOR_SELECTOR = 'input[type="radio"][name="properties[Couleur du prénom]"]';
  const states = new WeakMap();

  const sameElements = (left, right) =>
    left.length === right.length && left.every((element, index) => element === right[index]);

  const setupValidation = (nameInput) => {
    const formId = nameInput.getAttribute('form');
    const form = formId ? document.getElementById(formId) : nameInput.closest('form');
    if (!form) return;

    const button = form.querySelector('button[name="add"]');
    const buttonArea = button?.closest('.product-form-buttons');
    const colorRadios = [...document.querySelectorAll(COLOR_SELECTOR)].filter(
      (radio) => radio.getAttribute('form') === form.id || radio.form === form
    );

    if (!button || !buttonArea || colorRadios.length === 0) return;

    const previous = states.get(nameInput);
    if (
      previous?.button === button &&
      previous.status?.isConnected &&
      sameElements(previous.colorRadios, colorRadios)
    ) {
      return;
    }

    previous?.controller.abort();
    previous?.status.remove();
    if (previous?.button.dataset.aynaRequiredGate === 'true') {
      delete previous.button.dataset.aynaRequiredGate;
      if (previous.button.getAttribute('aria-disabled') === 'true') {
        previous.button.removeAttribute('aria-disabled');
      }
    }

    const controller = new AbortController();
    const status = document.createElement('p');
    status.className = 'ayna-required-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.id = nameInput.id + '-status';
    nameInput.insertAdjacentElement('afterend', status);
    nameInput.setAttribute('aria-describedby', [nameInput.getAttribute('aria-describedby'), status.id].filter(Boolean).join(' '));

    let missingName = true;
    let missingColor = true;

    const update = () => {
      missingName = nameInput.value.trim().length === 0;
      missingColor = !colorRadios.some((radio) => radio.checked);
      const incomplete = missingName || missingColor;

      if (incomplete) {
        button.dataset.aynaRequiredGate = 'true';
        button.setAttribute('aria-disabled', 'true');

        status.hidden = false;
        status.textContent = missingName && missingColor
          ? 'Indiquez le prénom et choisissez sa couleur pour continuer.'
          : missingName
            ? 'Indiquez le prénom pour continuer.'
            : 'Choisissez la couleur du prénom pour continuer.';
      } else {
        if (button.dataset.aynaRequiredGate === 'true') {
          delete button.dataset.aynaRequiredGate;
          if (button.getAttribute('aria-disabled') === 'true') {
            button.removeAttribute('aria-disabled');
          }
        }
        status.hidden = true;
        status.textContent = '';
      }
    };

    const stopIncompletePurchase = (event) => {
      update();
      const finish = document.querySelector('variant-picker input[data-option-name="Finitions"]:checked');
      if (!missingName && !missingColor && (!finish || finish.value === 'Sans finition')) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (finish && finish.value !== 'Sans finition') {
        document.querySelector('variant-picker input[data-option-name="Finitions"][value="Sans finition"]')?.click();
      } else if (missingName) {
        nameInput.focus();
      } else {
        colorRadios[0]?.focus();
      }
    };

    nameInput.addEventListener('input', update, { signal: controller.signal });
    nameInput.addEventListener('change', update, { signal: controller.signal });
    colorRadios.forEach((radio) =>
      radio.addEventListener('change', update, { signal: controller.signal })
    );
    button.addEventListener('click', stopIncompletePurchase, {
      capture: true,
      signal: controller.signal,
    });
    form.addEventListener('submit', stopIncompletePurchase, {
      capture: true,
      signal: controller.signal,
    });

    states.set(nameInput, { button, colorRadios, controller, status });
    update();
  };

  const initialise = () => document.querySelectorAll(NAME_SELECTOR).forEach(setupValidation);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }

  let framePending = false;
  const scheduleInitialise = () => {
    if (framePending) return;
    framePending = true;
    requestAnimationFrame(() => {
      framePending = false;
      initialise();
    });
  };

  document.addEventListener('shopify:section:load', scheduleInitialise);

  aynaConfiguratorUpdates.observe(scheduleInitialise);
})();


/* Ayna — ordre final : lettre principale, puis prénom. */
(() => {
  let pending = false;

  const syncDisplay = () => {
    const nativeField = document.querySelector(
      'variant-picker .variant-option[data-fieldset-index="0"]'
    );
    const displayField = document.querySelector('[data-ayna-letter-color-display]');
    if (!nativeField || !displayField) return;

    const selected = nativeField.querySelector('input[type="radio"]:checked')?.value;
    displayField.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = input.value === selected;
      input.setAttribute('aria-checked', input.checked ? 'true' : 'false');
    });
  };

  const ensureDisplay = () => {
    const nameColorField = document.querySelector(
      '.ayna-personalization .ayna-name-color:not([data-ayna-letter-color-display])'
    );
    const nativeField = document.querySelector(
      'variant-picker .variant-option[data-fieldset-index="0"]'
    );
    if (!nameColorField || !nativeField) return;

    nativeField.classList.add('ayna-letter-color-native');

    let displayField = document.querySelector('[data-ayna-letter-color-display]');
    if (!displayField) {
      displayField = nameColorField.cloneNode(true);
      displayField.classList.add('ayna-letter-color-display');
      displayField.setAttribute('data-ayna-letter-color-display', '');
      displayField.querySelector('legend').textContent = 'Couleur de la lettre principale *';
      displayField.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.name = 'ayna-letter-color-display';
        input.removeAttribute('form');
        input.removeAttribute('required');
        input.checked = false;
      });
      nameColorField.insertAdjacentElement('beforebegin', displayField);

      displayField.addEventListener('change', (event) => {
        const input = event.target.closest?.('input[type="radio"]');
        if (!input) return;
        const currentNative = document.querySelector(
          'variant-picker .variant-option[data-fieldset-index="0"] input[value="' + input.value + '"]'
        );
        if (currentNative && !currentNative.checked) currentNative.click();
        requestAnimationFrame(syncDisplay);
      });
    }

    if (displayField.nextElementSibling !== nameColorField) {
      nameColorField.insertAdjacentElement('beforebegin', displayField);
    }
    syncDisplay();
  };

  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      ensureDisplay();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureDisplay, { once: true });
  } else {
    ensureDisplay();
  }
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('variant:change', schedule);
  document.addEventListener('product:variant-change', schedule);
  aynaConfiguratorUpdates.observe(schedule);
  aynaConfiguratorUpdates.observe(ensureDisplay);
})();
;(() => {
  const STYLE_ID = 'ayna-product-final-fixes';
  const IMAGE_MAP = {
    Bleu: 'Lettre_liam_bleu',
    Beige: 'lettre_nicols_beige',
    Rose: 'Letre_ayna_rose',
    Vert: 'Lettre_Sohann_vert',
    Parme: 'Lettre_Laia_parme',
    Blanc: 'Lettre_rosalia_blanc'
  };

  const addStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      'fieldset[data-option-id*="-taille"]{margin-top:18px!important}',
      '.ayna-choice-disclosure>summary{display:grid!important;grid-template-columns:minmax(0,1fr) max-content 24px;align-items:center!important;column-gap:12px!important}',
      '.ayna-choice-disclosure>summary:after{justify-self:end}',
      '.ayna-choice-disclosure .ayna-finish-summary{justify-self:end;min-width:96px;text-align:right}',
      '.ayna-elephant-colors{display:none!important}',
      '.ayna-heart-badge{display:inline-flex;align-items:center;min-height:22px;margin-left:7px;padding:3px 8px;border:1px solid rgba(121,80,100,.28);border-radius:999px;background:#f7e4e7;color:#795064;font-size:10px;font-weight:750;letter-spacing:.02em;vertical-align:middle}',
      '@media(max-width:420px){fieldset[data-option-id*="-taille"]{margin-top:14px!important}.ayna-choice-disclosure>summary{grid-template-columns:minmax(0,1fr) max-content 22px;column-gap:8px!important}.ayna-choice-disclosure .ayna-finish-summary{min-width:78px;font-size:11px}}'
    ].join('');
    document.head.appendChild(style);
  };

  const showImage = color => {
    const marker = IMAGE_MAP[color];
    if (!marker) return;
    const thumb = [...document.querySelectorAll('.dialog-thumbnails-list__thumbnail')]
      .find(button => button.querySelector('img')?.src.includes(marker));
    if (thumb) thumb.click();
  };

  let lastColorThumbnail;
  let lastGalleryVariant;
  const showSelectedAfterRender = () => {
    const selectedColor = document.querySelector('input[name="ayna-letter-color-display"]:checked')?.value;
    const marker = IMAGE_MAP[selectedColor];
    if (!marker) return;
    const thumb = [...document.querySelectorAll('media-gallery .slideshow-controls__thumbnail')]
      .find(button => button.querySelector('img')?.src.includes(marker));
    const variantId = document.querySelector('product-form-component input[name="id"]')?.value;
    if (!thumb || (thumb === lastColorThumbnail && variantId === lastGalleryVariant)) return;
    const slideshow = thumb.closest('media-gallery')?.querySelector('slideshow-component');
    const index = [...document.querySelectorAll('media-gallery .slideshow-controls__thumbnail')].indexOf(thumb);
    if (typeof slideshow?.select !== 'function' || index < 0) return;
    lastColorThumbnail = thumb;
    lastGalleryVariant = variantId;
    slideshow.setAttribute('initial-slide', String(index));
    slideshow.select(index, undefined, { animate: false });
  };

  const showDefaultAyna = () => {
    if (document.documentElement.dataset.aynaDefaultMediaReady === 'true') return;
    const thumb = [...document.querySelectorAll('.dialog-thumbnails-list__thumbnail')]
      .find(button => button.querySelector('img')?.src.includes('Letre_ayna_rose'));
    if (!thumb) return;
    document.documentElement.dataset.aynaDefaultMediaReady = 'true';
    thumb.click();
  };

  // Wait for the native variant rendering before restoring the colour photo.
  Promise.all([import('@shopify/events'), import('@theme/utilities')]).then(([{ StandardEvents }, { viewTransition }]) => {
    document.addEventListener(StandardEvents.productSelect, event => {
      if (!event.target?.closest?.('.product-information')) return;
      event.promise?.then(async () => {
        if (viewTransition.current) await viewTransition.current;
        requestAnimationFrame(() => {
        lastColorThumbnail = undefined;
        showSelectedAfterRender();
        });
      }).catch(() => {});
    });
  }).catch(() => {});
  customElements.whenDefined('slideshow-component').then(() => requestAnimationFrame(() => {
    lastColorThumbnail = undefined;
    showSelectedAfterRender();
  }));

  const closeMaterialsOnce = () => {
    const details = [...document.querySelectorAll('details')]
      .find(item => item.querySelector(':scope>summary')?.textContent.trim() === 'Détails et matières');
    if (!details || details.dataset.aynaInitiallyClosed === 'true') return;
    details.removeAttribute('open');
    details.dataset.aynaInitiallyClosed = 'true';
  };

  const run = () => {
    addStyles();
    closeMaterialsOnce();
    showDefaultAyna();
    showSelectedAfterRender();
  };

  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      run();
    });
  };

  document.addEventListener('change', event => {
    const target = event.target;
    if (target?.matches?.('input[name="ayna-letter-color-display"]')) showImage(target.value);
    schedule();
  }, true);


  document.addEventListener('variant:change', schedule);
  document.addEventListener('product:variant-change', schedule);
  document.addEventListener('shopify:section:load', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
  aynaConfiguratorUpdates.observe(() => {
    showSelectedAfterRender();
    if (!document.getElementById(STYLE_ID)) schedule();
  });
})();

;(() => {
  const fixGuideLink = () => {
    document.querySelectorAll('a').forEach((link) => {
      if ((link.textContent || '').trim().startsWith('Voir comment la création prend forme')) {
        link.setAttribute('href', '/pages/comment-ca-marche#finitions');
      }
    });
  };
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', fixGuideLink, { once: true })
    : fixGuideLink();
  aynaConfiguratorUpdates.observe(fixGuideLink);
})();


(() => {
  const MAIN_SELECTOR = 'input[name="ayna-letter-color-display"]';
  const NAME_SELECTOR = 'input[type="radio"][name="properties[Couleur du prénom]"]';
  const COLORS = ['Bleu', 'Beige', 'Rose', 'Vert', 'Parme', 'Blanc'];
  const ALLOWED_NAME_COLORS = Object.freeze({ Bleu: ['Blanc'], Vert: ['Blanc'], Beige: ['Blanc'], Rose: ['Blanc'], Parme: ['Blanc'], Blanc: ['Bleu', 'Beige', 'Rose', 'Vert', 'Parme'] });

  const updateNameColorChoices = () => {
    if (window.location.pathname !== '/products/lettre-decorative-personnalise') return;

    const mainColor = document.querySelector(MAIN_SELECTOR + ':checked')?.value;
    const nameColorRadios = [...document.querySelectorAll(NAME_SELECTOR)];
    const allowedColors = ALLOWED_NAME_COLORS[mainColor];
    if (!allowedColors || nameColorRadios.length === 0) return;

    nameColorRadios.forEach((radio) => {
      const isAllowed = allowedColors.includes(radio.value);
      radio.disabled = !isAllowed;
      radio.closest('label')?.toggleAttribute('hidden', !isAllowed);
    });

    const selected = nameColorRadios.find((radio) => radio.checked);
    if (selected && selected.disabled) {
      selected.checked = false;
      selected.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (allowedColors.length === 1 && !nameColorRadios.some((radio) => radio.checked)) {
      const onlyChoice = nameColorRadios.find((radio) => radio.value === allowedColors[0]);
      if (onlyChoice) {
        onlyChoice.checked = true;
        onlyChoice.dispatchEvent(new Event('input', { bubbles: true }));
        onlyChoice.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    const fieldset = nameColorRadios[0]?.closest('fieldset');
    if (fieldset) {
      fieldset.dataset.aynaCompatibleWith = mainColor;
      fieldset.dataset.aynaSingleColor = String(allowedColors.length === 1);
      let note = fieldset.querySelector('.ayna-imposed-name-color');
      if (!note) {
        note = document.createElement('p');
        note.className = 'ayna-imposed-name-color';
        note.textContent = 'Le prénom sera blanc, pour contraster avec la lettre.';
        fieldset.append(note);
      }
      note.hidden = allowedColors.length !== 1;
    }
  };

  let pending = false;
  const scheduleUpdate = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      updateNameColorChoices();
    });
  };

  document.addEventListener('change', (event) => {
    if (event.target?.matches?.(MAIN_SELECTOR)) scheduleUpdate();
  }, true);
  document.addEventListener('shopify:section:load', scheduleUpdate);
  document.addEventListener('variant:change', scheduleUpdate);
  document.addEventListener('product:variant-change', scheduleUpdate);
  aynaConfiguratorUpdates.observe(scheduleUpdate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleUpdate, { once: true });
  } else {
    scheduleUpdate();
  }
})();


/* Ayna — synchronisation fiable des photos selon la couleur d

/* Ayna — synchronisation fiable des photos selon la couleur de la lettre */
(() => {
  if (window.__aynaColorGallerySyncInstalled) return;
  window.__aynaColorGallerySyncInstalled = true;

  const slides = Object.freeze({
    Bleu: 7,
    Rose: 4,
    Vert: 6,
    Parme: 5,
    Blanc: 8,
    Beige: 9
  });

  let timer;
  const showColorImage = (color) => {
    const slide = slides[color];
    if (!slide || !location.pathname.includes('/products/lettre-decorative-personnalise')) return;
    const selector = 'button[aria-label^="Diapositive ' + slide + ' sur"], button[aria-label^="Slide ' + slide + ' of"]';
    document.querySelector(selector)?.click();
  };

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('input[name="ayna-letter-color-display"]')) return;
    clearTimeout(timer);
    timer = setTimeout(() => showColorImage(input.value), 700);
  }, true);
})();


(() => {
  let ready = false;
  const normalize = () => {
    if (!ready) return;
    const picker = document.querySelector('variant-picker[data-product-handle="lettre-decorative-personnalise"]');
    const field = picker?.querySelector('input[data-option-name="Finitions"]')?.closest('fieldset');
    if (!field) return;
    field.hidden = true;
    const selected = field.querySelector('input:checked');
    const base = field.querySelector('input[value="Sans finition"]');
    if (base && selected !== base) base.click();
  };
  Promise.all(['variant-picker', 'product-price', 'product-form-component'].map(name => customElements.whenDefined(name))).then(() => {
    ready = true;
    normalize();
  });
  document.addEventListener('shopify:section:load', normalize);
  aynaConfiguratorUpdates.observe(normalize);
})();
